import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { useCallback, useContext } from "react";
import { useTextDomainContext } from "@/app/providers";

/**
 * Custom hook to delete a company.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{deleteCompany: Function, deleting: boolean}} - The function to delete a company and the loading state.
 */
export function useDeleteCompany(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [deleteCompany, { loading }] = useMutation<any>(
    mutations.deleteCompany,
    {
      onCompleted: (response) => {
        if (response?.deleteCompany?.success) {
          enqueueSnackbar(gettext("The company has been deleted."), {
            variant: "success",
          });
        } else {
          enqueueSnackbar(
            gettext("Failed to delete company. ") +
              (response?.deleteCompany?.message?.[0] || ""),
            {
              variant: "error",
            },
          );
        }
        onCompleted(response);
      },
      onError: (err) => {
        enqueueSnackbar(
          gettext("Error while deleting company: ") + err.message,
          {
            variant: "error",
          },
        );
        onError(err);
      },
      errorPolicy: "all",
      refetchQueries: ["GetCustomers", "GetGroups"],
    },
  );

  const deleteCompanyRow = useCallback(
    (variables: { companyId: string }) => {
      deleteCompany({
        variables,
      }).then();
    },
    [deleteCompany],
  );

  return {
    deleteCompany: deleteCompanyRow,
    deleting: loading,
  };
}
